import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Check } from 'lucide-react';
import GlitchText from './GlitchText';

const serviceDetails = {
  'lyrics': { name: 'BUY RHY LYRICS', description: 'Custom written lyrics tailored to your vision and style.' },
  'open-verse': { name: 'BUY RHY OPEN VERSE', description: 'An open verse feature ready for your track.' },
  'feature': { name: 'BUY RHY FEATURE', description: 'A full feature verse or hook for your song.' },
  'placement': { name: 'BUY RHY PLACEMENT', description: 'Get your music placed with RHY\'s network.' },
  'ep-mixtape': { name: 'BUY RHY EP OR MIXTAPE', description: 'Commission a full EP or mixtape project.' },
  'album': { name: 'BUY RHY ALBUM', description: 'Commission a complete album project.' },
  'live-performance': { name: 'BUY RHY LIVE PERFORMANCE', description: 'Book RHY for a live performance at your event.' },
  'singing-lessons': { name: 'BUY RHY SINGING LESSONS', description: 'Personal vocal coaching and singing lessons.' },
  'mixes': { name: 'BUY RHY MIXES', description: 'Professional mixing services by RHY.' },
};

const PricesPage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const service = serviceDetails[serviceId];

  if (!service) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl text-white mb-4 tracking-wider"><GlitchText>Service Not Found</GlitchText></h1>
          <button
            onClick={() => navigate('/')}
            className="font-mono text-sm text-white/60 hover:text-white transition-colors"
          >
            <GlitchText>← Back to Home</GlitchText>
          </button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          service: service.name,
          price: 'Budget TBD'
        })
      });
    } catch (error) {
      // Continue anyway for demo
    }

    setIsSubmitted(true);
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] py-12 px-6" data-testid="prices-page">
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className="fixed top-6 left-6 z-50 flex items-center gap-2 font-mono text-sm text-white/60 hover:text-white transition-colors"
        data-testid="back-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      <div className="max-w-2xl mx-auto pt-16">
        {/* Service Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4 tracking-wider">
            <GlitchText>{service.name}</GlitchText>
          </h1>
        </div>

        {/* Description */}
        <p className="font-mono text-center text-white/60 mb-8 max-w-lg mx-auto text-sm">
          <GlitchText>{service.description}</GlitchText>
        </p>

        {/* Fine Print Message */}
        <div className="mb-12 p-6 bg-white/5 border border-white/10 text-center">
          <p className="font-mono text-sm text-white/70 leading-relaxed">
            working with all budgets! however the more payment the more amazing things we can work on together to create. let&apos;s really collaborate for the love of music and creating moments of history that live on. if that&apos;s you{' '}
            <em className="text-white font-semibold not-italic">FILL OUT THE FORM BELOW</em>{' '}
            and reach out by sending an email i will always be checking as i am active on this email.
          </p>
        </div>

        {/* Contact Form */}
        {!isSubmitted ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block font-accent text-xs uppercase tracking-[0.2em] text-white/40 mb-2">
                Your Name
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-6 py-4 bg-white/5 border border-white/10 text-white placeholder-white/30 font-mono text-sm focus:outline-none focus:border-white/30 transition-colors"
                placeholder="Enter your name"
                data-testid="name-input"
              />
            </div>

            <div>
              <label className="block font-accent text-xs uppercase tracking-[0.2em] text-white/40 mb-2">
                Your Email
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-6 py-4 bg-white/5 border border-white/10 text-white placeholder-white/30 font-mono text-sm focus:outline-none focus:border-white/30 transition-colors"
                placeholder="Enter your email"
                data-testid="email-input"
              />
            </div>

            <div>
              <label className="block font-accent text-xs uppercase tracking-[0.2em] text-white/40 mb-2">
                Message / Details
              </label>
              <textarea
                required
                rows={5}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-6 py-4 bg-white/5 border border-white/10 text-white placeholder-white/30 font-mono text-sm focus:outline-none focus:border-white/30 transition-colors resize-none"
                placeholder="Tell us about your project and budget..."
                data-testid="message-input"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-8 py-4 bg-white text-black font-accent text-sm uppercase tracking-[0.2em] hover:bg-white/90 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="submit-btn"
            >
              {isLoading ? (
                <span className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
              ) : (
                <>
                  Submit Inquiry
                  <Send className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        ) : (
          <div className="text-center py-12 bg-white/5 border border-white/10">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 mb-6">
              <Check className="w-8 h-8 text-green-400" />
            </div>
            <h3 className="font-heading text-2xl font-bold text-white mb-2 tracking-wider">
              <GlitchText>Inquiry Submitted!</GlitchText>
            </h3>
            <p className="font-mono text-sm text-white/60">
              <GlitchText>Your request will be processed and reviewed shortly.</GlitchText>
            </p>
          </div>
        )}

        {/* Email Contact */}
        <div className="mt-12 text-center">
          <p className="font-mono text-xs text-white/40">
            Or email directly at{' '}
            <a href="mailto:rhy.inquiries@gmail.com" className="text-white/60 hover:text-white transition-colors">
              rhy.inquiries@gmail.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PricesPage;
